import { useState, useMemo, useCallback } from "react";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataVisualization } from "./DataVisualization";
import { HiddenContentSummary } from "./HiddenContentSummary";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";

// Helper to remove wrapping quotes from text
function removeWrappingQuotes(text) {
  if (!text || typeof text !== "string") return text;

  // Check if text starts and ends with quotes
  if (
    (text.startsWith('"') && text.endsWith('"')) ||
    (text.startsWith("'") && text.endsWith("'"))
  ) {
    return text.slice(1, -1);
  }

  return text;
}

function ReportDisplay({ report }) {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (sectionIndex) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionIndex]: !prev[sectionIndex],
    }));
  };

  // Helper function to check if content is unavailable
  const isDataUnavailable = useCallback((story) => {
    if (!story || typeof story !== "string") return false;
    const cleanStory = removeWrappingQuotes(story).toLowerCase().trim();
    return cleanStory === "data is not available." || cleanStory === "data is not available";
  }, []);

  // Process sections and slides to separate available from hidden
  const processedSections = useMemo(() => {
    if (!report?.plan) return { visibleSections: [], hiddenSections: [] };

    const result = report.plan.map((section) => {
      // Process slides within each section
      const visibleSlides = [];
      const hiddenSlides = [];

      if (section.slides) {
        section.slides.forEach((slide) => {
          if (isDataUnavailable(slide.story)) {
            hiddenSlides.push(slide);
          } else {
            visibleSlides.push(slide);
          }
        });
      }

      // Check if the entire section should be hidden (all slides are unavailable and section story is unavailable)
      const sectionHidden = isDataUnavailable(section.story) && visibleSlides.length === 0;

      return {
        ...section,
        visibleSlides,
        hiddenSlides,
        sectionHidden,
      };
    });

    const visibleSections = result.filter((s) => !s.sectionHidden);
    const hiddenSections = result.filter((s) => s.sectionHidden);

    return { visibleSections, hiddenSections };
  }, [report, isDataUnavailable]);

  if (!report) {
    return (
      <Alert>
        <AlertDescription>No report data available</AlertDescription>
      </Alert>
    );
  }

  // Check if report has the expected structure
  const hasStructuredContent = report?.plan && Array.isArray(report.plan);

  if (!hasStructuredContent) {
    // Fallback to JSON display for unstructured reports
    return (
      <Card>
        <CardHeader>
          <CardTitle>Report Results</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-h-[600px] overflow-y-auto">
            <pre className="p-4 bg-muted rounded-lg font-mono text-sm leading-relaxed whitespace-pre-wrap break-words">
              {JSON.stringify(report, null, 2)}
            </pre>
          </div>
        </CardContent>
        <CardFooter className="text-sm text-muted-foreground">
          Report generated at: {new Date().toLocaleString()}
        </CardFooter>
      </Card>
    );
  }

  // Render structured report
  return (
    <div className="space-y-6">
      {processedSections.visibleSections.map((section, sectionIndex) => {
        const isExpanded = expandedSections[sectionIndex] !== false; // Default to expanded

        return (
          <Card key={sectionIndex}>
            <CardHeader
              className="cursor-pointer select-none"
              onClick={() => toggleSection(sectionIndex)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CardTitle>{section.title || `Section ${sectionIndex + 1}`}</CardTitle>
                  <Badge variant="outline">{section.kind}</Badge>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSection(sectionIndex);
                  }}
                >
                  {isExpanded ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent className={cn("space-y-4", !isExpanded && "pb-0")}>
              {/* Section-level story - always visible unless it's "Data is not available." */}
              {section.story && !isDataUnavailable(section.story) && (
                <div className="text-foreground prose prose-sm prose-neutral dark:prose-invert max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {removeWrappingQuotes(section.story)}
                  </ReactMarkdown>
                </div>
              )}

              {/* Render visible slides only when expanded */}
              {isExpanded &&
                section.visibleSlides &&
                section.visibleSlides.map((slide, slideIndex) => (
                  <div key={slideIndex} className="pt-6 first:pt-0">
                    <div className="space-y-3">
                      {/* Slide story */}
                      {slide.story && (
                        <div className="text-foreground prose prose-sm prose-neutral dark:prose-invert max-w-none">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {removeWrappingQuotes(slide.story)}
                          </ReactMarkdown>
                        </div>
                      )}

                      {/* Data visualization */}
                      {slide.data && (
                        <div className="mt-4">
                          <DataVisualization data={slide.data} />
                        </div>
                      )}
                    </div>
                  </div>
                ))}

              {/* Show hidden slides summary at the bottom of the section */}
              {isExpanded && section.hiddenSlides && section.hiddenSlides.length > 0 && (
                <HiddenContentSummary hiddenItems={section.hiddenSlides} type="slides" />
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Show hidden sections summary at the bottom of the report */}
      {processedSections.hiddenSections && processedSections.hiddenSections.length > 0 && (
        <HiddenContentSummary hiddenItems={processedSections.hiddenSections} type="sections" />
      )}
    </div>
  );
}

export default ReportDisplay;
